import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Input } from "@/components/ui/input";
import HebrewKeyboard from "@/components/HebrewKeyboard";
import { Button } from "@/components/ui/button";
import NoIndex from "@/components/NoIndex";
import { ProgressDots } from "@/components/SlideShell";
import SlideEditor from "@/components/SlideEditor";
import { useAuth } from "@/lib/auth";
import useLiveStream from "@/lib/useLiveStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

// Act 1 Scene 3 pitch palette — must match VOLUNTEER_PITCHES in server.py.
const PITCHES = ["C", "C♯", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];

// Act 3 Scene 1 staff demos. The parsha button is built at render time
// from the event's parsha_name (set in the slides planner).
const HEBREW_DEMOS = [
  { id: "alefbet", label: "The whole aleph-bet", payload: { kind: "alefbet", label: "The aleph-bet" } },
  { id: "torah", label: "תורה", payload: { kind: "word", text: "תורה", label: "Torah" } },
  { id: "mitzvah", label: "מצוה", payload: { kind: "word", text: "מצוה", label: "Mitzvah" } },
];

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.floor(diff))}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

/**
 * Dan's moderator console. One-hand friendly: the pending queue lives on
 * the LEFT with big tap-target "Play this" buttons; the RIGHT column has
 * the currently-on-display card, a self-inject input, and the mode toggle.
 */
export default function LiveModerator() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loadErr, setLoadErr] = useState("");
  const [selfInject, setSelfInject] = useState("");
  const [pushing, setPushing] = useState(false);
  const [tick, setTick] = useState(0); // for re-rendering timeAgo
  const { connected, submissions, setSubmissions, lastSelection, setLastSelection, mode, setMode, event, applyEvent } =
    useLiveStream(slug);

  // ONE authoritative event, owned by the stream hook. Every HTTP response
  // (nav, toggles, the initial fetch) goes through `applyEvent` — the same
  // monotonic merge the stream and the 4s reconcile poll use. Previously the
  // page read `liveEvent || event`: once any stream frame landed, the panel's
  // own nav responses were written to state nothing read, so a single missed
  // frame left "Next" computing from a stale slide.
  const effectiveEvent = event;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/app/login", { state: { from: `/live/${slug}/admin` } });
      return;
    }
    (async () => {
      try {
        const [ev, subs, cur] = await Promise.all([
          axios.get(`${API}/live/${slug}`),
          ax.get(`/admin/live/${slug}/submissions`),
          axios.get(`${API}/live/${slug}/selections/current`),
        ]);
        applyEvent(ev.data);
        setSubmissions(subs.data);
        if (cur.data.selection) setLastSelection(cur.data.selection);
      } catch (e) {
        if (e.response?.status === 403) setLoadErr("This account isn't on the live-admin allowlist.");
        else if (e.response?.status === 404) setLoadErr("Event not found.");
        else setLoadErr("Couldn't load the event.");
      }
    })();
  }, [authLoading, user, slug]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  const pending = useMemo(
    () =>
      submissions.filter(
        (s) =>
          s.status !== "selected" &&
          s.status !== "skipped" &&
          // Filter by the current event mode so English suggestions hide
          // while Dan is in Hebrew mode and reappear when he switches back.
          // Legacy rows without a `mode` field (shouldn't happen) fall
          // through unfiltered so nothing goes silently missing.
          (!s.mode || s.mode === mode)
      ),
    // tick forces re-eval so timeAgo() text refreshes; suppression not needed
    // because tick is intentionally part of the deps.
    [submissions, tick, mode]
  );

  const pushSubmission = async (sub) => {
    try {
      await ax.post(`/admin/live/${slug}/selections`, { submission_id: sub.id });
      setSubmissions((prev) => prev.map((s) => s.id === sub.id ? { ...s, status: "selected" } : s));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't push.");
    }
  };

  const skipSubmission = async (sub) => {
    // Optimistic UI update; server call in the background so a refresh
    // won't rewind the queue. If it fails, roll back and toast.
    setSubmissions((prev) => prev.map((s) => s.id === sub.id ? { ...s, status: "skipped" } : s));
    try {
      await ax.patch(`/admin/live/${slug}/submissions/${sub.id}`, { status: "skipped" });
    } catch (e) {
      setSubmissions((prev) => prev.map((s) => s.id === sub.id ? { ...s, status: "pending" } : s));
      toast.error(e.response?.data?.detail || "Couldn't skip.");
    }
  };

  const pushSelfInject = async (e) => {
    e.preventDefault();
    const cleaned = selfInject.split(/\s+/).map((w) => w.trim()).filter(Boolean).slice(0, 5);
    if (!cleaned.length) return;
    setPushing(true);
    try {
      await ax.post(`/admin/live/${slug}/selections`, { words: cleaned });
      setSelfInject("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't push.");
    } finally {
      setPushing(false);
    }
  };

  const toggleMode = async () => {
    const next = mode === "english" ? "hebrew" : "english";
    try {
      await ax.patch(`/admin/live/${slug}/mode`, { mode: next });
      // SSE will echo back, but update locally too so UI reacts instantly.
      setMode(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't switch modes.");
    }
  };

  const togglePause = async () => {
    if (!event) return;
    try {
      const { data } = await ax.patch(`/admin/live/events/${slug}`, { is_active: !event.is_active });
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't toggle event.");
    }
  };

  // Fantasia visual toggles pushed to the live display via SSE
  // (`event_updated`). We apply the server's copy rather than flipping
  // locally first — a local guess has no `rev`, so it would be discarded by
  // the monotonic merge (and could fight the reconcile poll).
  const toggleFantasia = async (field) => {
    if (!event) return;
    try {
      const { data } = await ax.patch(`/admin/live/events/${slug}`, { [field]: !event[field] });
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't toggle Fantasia.");
    }
  };

  // Presentation slide navigation. `event` is the initial fetch, but
  // `effectiveEvent` (SSE-pushed) is more current if another tab has
  // moved the show forward.
  const eventSlides = effectiveEvent?.slides || [];
  const currentSlideKey = effectiveEvent?.current_slide_key;
  const currentSlideIdx = eventSlides.findIndex((s) => s.key === currentSlideKey);
  const currentSlide = eventSlides[currentSlideIdx];
  const navToSlide = async (key) => {
    if (!key) return;
    try {
      const { data } = await ax.post(`/admin/live/events/${slug}/nav`, { slide_key: key });
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't advance the slide.");
    }
  };

  // Next / Prev send a DIRECTION, so the server steps from its own position.
  // A panel that missed an update can no longer re-send the slide it thinks
  // it's on (which read as "the button did nothing") or jump two at once.
  const [navving, setNavving] = useState(false);
  const navStep = async (direction) => {
    if (navving) return;
    setNavving(true);
    try {
      const { data } = await ax.post(`/admin/live/events/${slug}/nav`, { direction });
      applyEvent(data);
    } catch (e) {
      // 409 = already at the end of the deck; not worth a scary toast.
      if (e.response?.status !== 409) {
        toast.error(e.response?.data?.detail || "Couldn't advance the slide.");
      }
    } finally {
      setNavving(false);
    }
  };

  const [editorScope, setEditorScope] = useState(null); // null | "event" — master template lives on /live-admin
  const [showHebrewKb, setShowHebrewKb] = useState(false);

  // The audience-facing scenes are the only ones where the submission
  // queue, "now on display" and self-inject controls are useful.
  const isAudienceScene =
    currentSlide?.scene_id === "act2-scene3" || currentSlide?.scene_id === "act3-scene2";
  // Landing on an app slide stamps `selection_cleared_at`, which wipes the
  // staff — so anything selected before that stamp is no longer showing.
  const clearedAtMs = effectiveEvent?.selection_cleared_at
    ? new Date(effectiveEvent.selection_cleared_at).getTime()
    : 0;
  const showingSelection =
    lastSelection && new Date(lastSelection.selected_at).getTime() > clearedAtMs
      ? lastSelection
      : null;

  // Recap playback controls — Dan clicks Play on the moderator once he
  // hits the Act 3 Scene 3 slide. The backend stamps a wall-clock start
  // time; both the display's collage and this panel derive the reveal
  // cadence (15s per note) from that timestamp.
  const recapStartedAt = effectiveEvent?.recap_playback_started_at || null;
  const recapPlaying = !!recapStartedAt;
  const playRecap = async () => {
    try {
      const { data } = await ax.post(`/admin/live/events/${slug}/recap/play`);
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start recap.");
    }
  };
  const resetRecap = async () => {
    try {
      const { data } = await ax.post(`/admin/live/events/${slug}/recap/reset`);
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reset recap.");
    }
  };

  // Act 1 Scene 3 — a volunteer calls out four pitches; Dan taps them
  // here. They push live to the slide via SSE and ride along into the
  // recap post-it.
  const volunteerNotes = effectiveEvent?.volunteer_notes || [];
  const [notesSaving, setNotesSaving] = useState(false);
  const saveVolunteerNotes = async (next) => {
    setNotesSaving(true);
    try {
      const { data } = await ax.patch(`/admin/live/events/${slug}`, { volunteer_notes: next });
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the notes.");
    } finally {
      setNotesSaving(false);
    }
  };

  // Act 2 Scene 1 — spin the wheel. The 7 song titles are scripted on
  // the slide itself (edit via Slides); here Dan just taps whichever
  // one the wheel landed on so it lands on the recap post-it.
  const wheelSongs = useMemo(() => {
    const s = (effectiveEvent?.slides || []).find((x) => x.key === "act2-scene1");
    if (!s) return [];
    return [1, 2, 3, 4, 5, 6, 7]
      .map((n) => (s[`wheel_song_${n}`] || "").trim())
      .filter(Boolean);
  }, [effectiveEvent?.slides]);
  const wheelPick = effectiveEvent?.wheel_pick || "";
  const saveWheelPick = async (title) => {
    try {
      const { data } = await ax.patch(`/admin/live/events/${slug}`, { wheel_pick: title });
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the wheel pick.");
    }
  };

  // Act 3 Scene 1 — Hebrew staff demos. Each push stamps a server-side
  // start time; the display reveals one letter every 300ms from it.
  const hebrewDemo = effectiveEvent?.hebrew_demo || null;
  const parshaName = (effectiveEvent?.parsha_name || "").trim();
  const pushHebrewDemo = async (payload) => {
    try {
      const { data } = await ax.post(`/admin/live/events/${slug}/hebrew-demo`, payload);
      applyEvent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't push to the staff.");
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen w-full grid place-items-center p-6" style={{ backgroundColor: "#080419", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }}>
        <NoIndex title="Compose Yourself! by Dan Berry" /><p data-testid="live-mod-error">{loadErr}</p>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="min-h-screen w-full grid place-items-center" style={{ backgroundColor: "#080419", color: "#F5EFE3" }}>
        <NoIndex title="Compose Yourself! by Dan Berry" /><p className="text-sm opacity-60">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: "#080419", color: "#F5EFE3", fontFamily: "'Courier Prime', monospace" }} data-live-dark="" data-testid="live-mod-page">
      <NoIndex title="Compose Yourself! by Dan Berry" />
      <Toaster position="bottom-right" richColors />
      <header className="max-w-[1400px] mx-auto px-6 pt-6 pb-4 flex flex-wrap items-center gap-4 justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "#F2D589" }}>
            Moderator · <span data-testid="live-mod-connection">{connected ? "● live" : "○ reconnecting"}</span>
          </p>
          <h1 className="text-2xl sm:text-3xl mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="live-mod-title">
            {event.name}
          </h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-xs opacity-60">mode:</span>
          <Button type="button" onClick={toggleMode} data-testid="live-mod-mode-toggle" className="h-10 rounded-full text-xs" style={{ backgroundColor: mode === "hebrew" ? "#F2D589" : "rgba(255,255,255,0.08)", color: mode === "hebrew" ? "#080419" : "#F5EFE3", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
            {mode === "hebrew" ? "עברית / Hebrew" : "English"}
          </Button>
          <Button type="button" variant="outline" onClick={togglePause} data-testid="live-mod-pause-toggle" className="h-10 rounded-full text-xs border-white/20">
            {event.is_active ? "Pause floor" : "Open floor"}
          </Button>
          {/* Fantasia visual layer toggles. Ghost letters is the
              painterly Hebrew-letter drift; Smoke is the WebGL fluid
              splat under each note. Both push live via SSE. */}
          <span className="text-xs opacity-60 ml-2">fantasia:</span>
          <Button
            type="button"
            onClick={() => toggleFantasia("fantasia_ghosts_on")}
            data-testid="live-mod-fantasia-ghosts-toggle"
            className="h-10 rounded-full text-xs"
            style={{
              backgroundColor: event.fantasia_ghosts_on ? "#F2D589" : "rgba(255,255,255,0.08)",
              color: event.fantasia_ghosts_on ? "#080419" : "#F5EFE3",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {event.fantasia_ghosts_on ? "Ghosts on" : "Ghosts off"}
          </Button>
          <Button
            type="button"
            onClick={() => toggleFantasia("fantasia_smoke_on")}
            data-testid="live-mod-fantasia-smoke-toggle"
            className="h-10 rounded-full text-xs"
            style={{
              backgroundColor: event.fantasia_smoke_on ? "#F2D589" : "rgba(255,255,255,0.08)",
              color: event.fantasia_smoke_on ? "#080419" : "#F5EFE3",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {event.fantasia_smoke_on ? "Smoke on" : "Smoke off"}
          </Button>
        </div>
      </header>

      {/* Presentation controls — prev/next + progress dots + current
          slide label. Sits above the incoming queue so Dan can drive the
          show from one screen. */}
      <section
        className="max-w-[1400px] mx-auto px-6 pb-4"
        data-testid="live-mod-presentation"
      >
        <div
          className="rounded-2xl border px-5 py-4 flex flex-wrap items-center gap-4 justify-between"
          style={{ borderColor: "rgba(232,196,138,0.28)", backgroundColor: "transparent" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-[10px] uppercase tracking-[0.32em] opacity-60">On screen</span>
            <span
              className="text-sm truncate max-w-[280px]"
              data-testid="live-mod-current-slide"
              title={currentSlide?.headline || currentSlide?.key || ""}
            >
              {currentSlide?.scene_label || currentSlide?.act_label || currentSlide?.key || "—"}
              {currentSlide?.headline ? (
                <span className="opacity-60"> · {currentSlide.headline}</span>
              ) : null}
            </span>
          </div>
          <ProgressDots
            slides={eventSlides}
            currentSlideKey={currentSlideKey}
            onJump={navToSlide}
          />
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditorScope("event")}
              data-testid="live-mod-open-event-editor"
              className="h-10 rounded-full text-xs border-white/20"
            >
              Slides
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navStep("prev")}
              disabled={navving || currentSlideIdx === 0}
              data-testid="live-mod-slide-prev"
              className="h-10 rounded-full text-xs border-white/20"
            >
              ← Prev
            </Button>
            <Button
              type="button"
              onClick={() => navStep("next")}
              disabled={navving || (currentSlideIdx >= 0 && currentSlideIdx === eventSlides.length - 1)}
              data-testid="live-mod-slide-next"
              className="h-10 rounded-full text-xs"
              style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase" }}
            >
              Next →
            </Button>
          </div>
        </div>
        {/* Speaking notes for the live slide — moderator only, never on
            the projector. */}
        {(currentSlide?.speaker_notes || "").trim() ? (
          <div
            className="mt-3 rounded-2xl border px-5 py-4"
            style={{ borderColor: "rgba(245,239,227,0.18)", backgroundColor: "transparent" }}
            data-testid="live-mod-speaker-notes"
          >
            <div className="text-[10px] uppercase tracking-[0.32em] opacity-60 mb-2">
              Speaking notes
            </div>
            <p
              className="whitespace-pre-wrap text-base leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#F5EFE3" }}
            >
              {currentSlide.speaker_notes}
            </p>
          </div>
        ) : null}
        {/* Recap playback — only on the recap slide. Location + parsha
            live in the slides planner (Slides → Show details). */}
        {currentSlideKey === "act3-scene3" ? (
        <div
          className="mt-3 rounded-2xl border px-5 py-4 flex flex-wrap items-center gap-3"
          style={{ borderColor: "rgba(232,196,138,0.28)", backgroundColor: "transparent" }}
          data-testid="live-mod-recap-seeds"
        >
          <span className="text-[10px] uppercase tracking-[0.32em] opacity-60">
            Act 3 · Scene 3 — recap
          </span>
          {/* Playback controls — visible always so Dan can prime the
              collage or reset it from anywhere. Actual reveal only
              matters while the display is showing act3-scene3. */}
          <div className="flex items-center gap-2" data-testid="live-mod-recap-playback">
            <Button
              type="button"
              onClick={playRecap}
              disabled={recapPlaying}
              data-testid="live-mod-recap-play"
              className="h-10 rounded-full text-xs px-5"
              style={{
                backgroundColor: recapPlaying ? "rgba(167,116,255,0.18)" : "#A774FF",
                color: recapPlaying ? "#A774FF" : "#241246",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              {recapPlaying ? "Playing…" : "▶ Play recap"}
            </Button>
            <Button
              type="button"
              onClick={resetRecap}
              disabled={!recapPlaying}
              data-testid="live-mod-recap-reset"
              className="h-10 rounded-full text-xs"
              style={{
                backgroundColor: "rgba(255,255,255,0.08)",
                color: recapPlaying ? "#F5EFE3" : "rgba(245,239,227,0.35)",
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Reset
            </Button>
          </div>
          <p className="text-[11px] opacity-50 ml-auto max-w-[300px]" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
            Location + parsha live in Slides → Show details. They collage with the scene titles and the first/last audience picks.
          </p>
        </div>
        ) : null}
        {/* Act 1 Scene 3 — volunteer's four pitches. */}
        {currentSlideKey === "act1-scene3" ? (
        <div
          className="mt-3 rounded-2xl border px-5 py-4"
          style={{ borderColor: "rgba(167,116,255,0.28)", backgroundColor: "transparent" }}
          data-testid="live-mod-volunteer-notes"
        >
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.32em] opacity-60">
              Act 1 · Scene 3 — volunteer's 4 notes
            </span>
            <span
              className="text-lg tracking-[0.2em]"
              style={{ color: "#A774FF", fontFamily: "'Frank Ruhl Libre', serif" }}
              data-testid="live-mod-volunteer-notes-picked"
            >
              {volunteerNotes.length ? volunteerNotes.join("  ·  ") : "— — — —"}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                type="button"
                onClick={() => saveVolunteerNotes(volunteerNotes.slice(0, -1))}
                disabled={!volunteerNotes.length || notesSaving}
                data-testid="live-mod-volunteer-undo"
                className="h-9 rounded-full text-xs bg-white/[0.08] text-white hover:bg-white/[0.14]"
                style={{ letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}
              >
                Undo
              </Button>
              <Button
                type="button"
                onClick={() => saveVolunteerNotes([])}
                disabled={!volunteerNotes.length || notesSaving}
                data-testid="live-mod-volunteer-clear"
                className="h-9 rounded-full text-xs bg-white/[0.08] text-white hover:bg-white/[0.14]"
                style={{ letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}
              >
                Clear
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {PITCHES.map((p) => {
              const full = volunteerNotes.length >= 4;
              return (
                <button
                  key={p}
                  type="button"
                  disabled={full || notesSaving}
                  onClick={() => saveVolunteerNotes([...volunteerNotes, p])}
                  data-testid={`live-mod-pitch-${p}`}
                  className="h-11 min-w-[52px] px-3 rounded-xl text-base transition-colors disabled:opacity-30"
                  style={{
                    backgroundColor: "rgba(167,116,255,0.12)",
                    border: "1px solid rgba(167,116,255,0.35)",
                    color: "#FFD9C2",
                    fontFamily: "'Frank Ruhl Libre', serif",
                  }}
                >
                  {p}
                </button>
              );
            })}
          </div>
        </div>
        ) : null}
        {/* Act 2 Scene 1 — spin the wheel song pick. Only while that
            slide is live so the panel isn't in the way all show. */}
        {currentSlideKey === "act2-scene1" ? (
        <div
          className="mt-3 rounded-2xl border px-5 py-4"
          style={{ borderColor: "rgba(252,160,122,0.28)", backgroundColor: "transparent" }}
          data-testid="live-mod-wheel"
        >
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="text-[10px] uppercase tracking-[0.32em] opacity-60">
              Act 2 · Scene 1 — spin the wheel
            </span>
            <span
              className="text-base"
              style={{ color: "#FCA07A", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
              data-testid="live-mod-wheel-picked"
            >
              {wheelPick || "nothing picked yet"}
            </span>
            <Button
              type="button"
              onClick={() => saveWheelPick("")}
              disabled={!wheelPick}
              data-testid="live-mod-wheel-clear"
              className="h-9 rounded-full text-xs ml-auto bg-white/[0.08] text-white hover:bg-white/[0.14]"
              style={{ letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}
            >
              Clear
            </Button>
          </div>
          {wheelSongs.length ? (
            <div className="flex flex-wrap gap-2">
              {wheelSongs.map((title, i) => {
                const active = wheelPick === title;
                return (
                  <button
                    key={`${title}-${i}`}
                    type="button"
                    onClick={() => saveWheelPick(title)}
                    data-testid={`live-mod-wheel-song-${i + 1}`}
                    className="h-11 px-4 rounded-xl text-sm transition-colors"
                    style={{
                      backgroundColor: active ? "#FCA07A" : "rgba(252,160,122,0.12)",
                      border: "1px solid rgba(252,160,122,0.35)",
                      color: active ? "#2A0E05" : "#DDEAEC",
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: active ? 700 : 500,
                    }}
                  >
                    {title}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] opacity-50" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
              No songs scripted yet — add them on the Act 2 Scene 1 slide via the "Slides" editor.
            </p>
          )}
        </div>
        ) : null}
        {/* Act 3 Scene 1 — Hebrew staff demos pushed to the display. */}
        {currentSlideKey === "act3-scene1" ? (
          <div
            className="mt-3 rounded-2xl border px-5 py-4"
            style={{ borderColor: "rgba(183,156,232,0.3)", backgroundColor: "rgba(183,156,232,0.05)" }}
            data-testid="live-mod-hebrew-demo"
          >
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="text-[10px] uppercase tracking-[0.32em] opacity-60">
                Act 3 · Scene 1 — lay it on the staff
              </span>
              <span
                className="text-base"
                style={{ color: "#B79CE8", fontFamily: "'Frank Ruhl Libre', serif" }}
                data-testid="live-mod-hebrew-demo-current"
              >
                {hebrewDemo ? (hebrewDemo.label || hebrewDemo.text) : "staff is empty"}
              </span>
              <Button
                type="button"
                onClick={() => pushHebrewDemo({ kind: "clear" })}
                disabled={!hebrewDemo}
                data-testid="live-mod-hebrew-demo-clear"
                className="h-9 rounded-full text-xs ml-auto bg-white/[0.08] text-white hover:bg-white/[0.14]"
                style={{ letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600 }}
              >
                Clear staff
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {HEBREW_DEMOS.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => pushHebrewDemo(d.payload)}
                  data-testid={`live-mod-hebrew-demo-${d.id}`}
                  className="h-12 px-5 rounded-xl transition-colors"
                  style={{
                    backgroundColor: "rgba(183,156,232,0.14)",
                    border: "1px solid rgba(183,156,232,0.4)",
                    color: "#EFE6FF",
                    fontFamily: "'Frank Ruhl Libre', serif",
                    fontSize: 18,
                  }}
                >
                  {d.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => pushHebrewDemo({ kind: "word", text: parshaName, label: "The parsha" })}
                disabled={!parshaName}
                data-testid="live-mod-hebrew-demo-parsha"
                className="h-12 px-5 rounded-xl transition-colors disabled:opacity-30"
                style={{
                  backgroundColor: "rgba(183,156,232,0.14)",
                  border: "1px solid rgba(183,156,232,0.4)",
                  color: "#EFE6FF",
                  fontFamily: "'Frank Ruhl Libre', serif",
                  fontSize: 18,
                }}
              >
                {parshaName || "Parsha (set it in Slides)"}
              </button>
            </div>
            <p className="text-[11px] opacity-50 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}>
              Letters land one every 0.3s — silent, so you play along.
            </p>
          </div>
        ) : null}
      </section>

      {editorScope ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
          style={{ backgroundColor: "rgba(5,2,17,0.85)", backdropFilter: "blur(6px)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditorScope(null);
          }}
          data-testid="slide-editor-modal"
        >
          <div className="w-full pt-10 pb-20" onClick={(e) => e.stopPropagation()}>
            <SlideEditor
              scope={editorScope}
              slug={slug}
              onClose={() => setEditorScope(null)}
            />
          </div>
        </div>
      ) : null}

      <main className={`max-w-[1400px] mx-auto px-6 pb-16 grid gap-6 ${isAudienceScene ? "lg:grid-cols-[1.5fr_1fr]" : "grid-cols-1"}`}>
        {isAudienceScene ? (
        <>
        {/* Left: pending queue */}
        <section className="rounded-2xl border p-4 backdrop-blur-md" style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.45)" }}>
          <h2 className="text-lg mb-3 flex items-baseline justify-between" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <span>Incoming ({pending.length})</span>
            <span className="text-[10px] uppercase tracking-[0.28em] opacity-50">tap to send</span>
          </h2>
          {pending.length === 0 ? (
            <p className="text-sm opacity-60 italic" data-testid="live-mod-empty">Waiting for submissions…</p>
          ) : (
            <ul className="space-y-2" data-testid="live-mod-queue">
              {pending.map((s) => (
                <li key={s.id} className="rounded-xl border p-3 flex items-center gap-3" style={{ borderColor: "rgba(232,196,138,0.25)", backgroundColor: "rgba(232,196,138,0.05)" }} data-testid={`live-mod-sub-${s.id}`}>
                  <button
                    type="button"
                    onClick={() => pushSubmission(s)}
                    className="flex-1 text-left min-w-0"
                    data-testid={`live-mod-play-${s.id}`}
                  >
                    <p className="text-xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }} dir={s.mode === "hebrew" ? "rtl" : "ltr"}>{s.words.join(" ")}</p>
                    <p className="text-[10px] uppercase tracking-[0.22em] mt-1 opacity-60">
                      {s.participant_name || "anon"} · {timeAgo(s.created_at)}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => pushSubmission(s)}
                    className="h-12 px-4 rounded-full shrink-0 text-xs font-semibold"
                    style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", letterSpacing: "0.18em", textTransform: "uppercase" }}
                    data-testid={`live-mod-push-${s.id}`}
                  >
                    Play ▶
                  </button>
                  <button type="button" onClick={() => skipSubmission(s)} className="text-[10px] uppercase tracking-[0.22em] opacity-50 hover:opacity-100 px-1 shrink-0" data-testid={`live-mod-skip-${s.id}`}>skip</button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Right: now-showing + self inject */}
        <aside className="space-y-4">
          <div className="rounded-2xl border p-5 backdrop-blur-md" style={{ borderColor: "rgba(232,196,138,0.4)", backgroundColor: "rgba(8,4,25,0.55)" }} data-testid="live-mod-current">
            <p className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "#F2D589" }}>Now on display</p>
            {showingSelection ? (
              <>
                <p className="text-3xl leading-tight mt-2" style={{ fontFamily: "'Cormorant Garamond', serif" }} dir={showingSelection.mode === "hebrew" ? "rtl" : "ltr"} data-testid="live-mod-current-words">
                  {showingSelection.words.join(" ")}
                </p>
                <p className="text-[10px] uppercase tracking-[0.22em] opacity-60 mt-1">
                  {showingSelection.source === "admin" ? "Dan self-inject" : showingSelection.participant_name} · {timeAgo(showingSelection.selected_at)}
                </p>
              </>
            ) : (
              <p className="text-sm opacity-60 italic mt-2">Nothing on display yet.</p>
            )}
          </div>

          <form onSubmit={pushSelfInject} className="rounded-2xl border p-5 backdrop-blur-md space-y-3" style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,25,0.45)" }} data-testid="live-mod-selfinject-form">
            <p className="text-[10px] uppercase tracking-[0.32em] opacity-70">Dan&apos;s own words (song 3)</p>
            <Input
              value={selfInject}
              onChange={(e) => setSelfInject(e.target.value)}
              placeholder={mode === "hebrew" ? "שלום אהבה" : "your own words"}
              dir={mode === "hebrew" ? "rtl" : "ltr"}
              className="h-12 text-lg bg-white/[0.06] border-white/15"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="live-mod-selfinject-input"
            />
            {mode === "hebrew" ? (
              <div dir="ltr" className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowHebrewKb((s) => !s)}
                  data-testid="live-mod-hebrew-keyboard-toggle"
                  className="h-9 rounded-full border-white/20 text-xs"
                >
                  {showHebrewKb ? "Hide Hebrew keyboard" : "Hebrew keyboard"}
                </Button>
                {showHebrewKb ? (
                  <HebrewKeyboard
                    onLetter={(ch) => setSelfInject((v) => v + ch)}
                    onSpace={() => setSelfInject((v) => `${v} `)}
                    onBackspace={() => setSelfInject((v) => v.slice(0, -1))}
                  />
                ) : null}
              </div>
            ) : null}
            <Button type="submit" disabled={pushing} data-testid="live-mod-selfinject-push" className="w-full h-12 rounded-full" style={{ backgroundColor: "transparent", color: "#F2D589", border: "1px solid #F2D589", fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", fontSize: "12px" }}>
              {pushing ? "…" : "Push to display →"}
            </Button>
          </form>
        </aside>
        </>
        ) : null}

        <div className="text-xs opacity-60 space-y-1 px-1">
          <p>Audience URL: <code className="opacity-100">/live/{slug}</code></p>
          <p>Display URL: <code className="opacity-100">/live/{slug}/display</code></p>
        </div>
      </main>
    </div>
  );
}
